/**
 * Format an openapi-fetch error body into an operator-readable string
 * (GH #2240). The pages rendered the error body via `String(apiError)`, which
 * on the canonical `{ success:false, error:{ code, message } }` envelope
 * produces the literal "[object Object]" -- and, being truthy, suppressed the
 * `|| fallback`. This turns any of the shapes the platform actually returns
 * into "STATUS CODE: message", and NEVER returns "[object Object]".
 */

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * @param apiError the openapi-fetch `error` body (unknown shape)
 * @param fallback used only when no message can be extracted
 * @param status optional HTTP status from the fetch `response`
 */
export function formatApiError(
  apiError: unknown,
  fallback: string,
  status?: number,
): string {
  const prefix = status ? `${status} ` : '';

  // Plain-string body: return it as-is (with the status prefix).
  if (typeof apiError === 'string' && apiError.trim() !== '') {
    return `${prefix}${apiError}`.trim();
  }

  if (isRecord(apiError)) {
    // Canonical ErrorResponse envelope: { success:false, error:{ code, message } }
    const nested = apiError.error;
    if (isRecord(nested)) {
      const codeStr = typeof nested.code === 'string' && nested.code ? nested.code : '';
      const msgStr =
        typeof nested.message === 'string' && nested.message ? nested.message : '';
      if (msgStr) {
        return `${prefix}${codeStr ? `${codeStr}: ` : ''}${msgStr}`.trim();
      }
      if (codeStr) {
        return `${prefix}${codeStr}`.trim();
      }
    }

    // Middleware shape: { error: "message string" }
    if (typeof nested === 'string' && nested.trim() !== '') {
      return `${prefix}${nested}`.trim();
    }

    // Bare { message: "..." } shape.
    const flat = apiError.message;
    if (typeof flat === 'string' && flat.trim() !== '') {
      return `${prefix}${flat}`.trim();
    }
  }

  // Never "[object Object]": fall back to the caller's copy (with status if known).
  return status ? `${prefix}${fallback}`.trim() : fallback;
}
